// src/utils/tasks/postQuestionnaireDestination.js
/**
 * Where a finished questionnaire sends the user.
 *
 * Straight back to the questionnaire list is a dead end: the work it was part
 * of lives on the To do list, and once that list is clear there is nothing to
 * come back to. So the answer is the To do list while anything is still
 * outstanding, and Home when the submission cleared the last of it.
 */

import { PROTOCOL_TASK_STATES } from "../protocolTasks";
import {
  applyOptimisticTaskCompletions,
  recordOptimisticTaskCompletion,
} from "./optimisticTaskCompletion";

export const TASKS_OUTSTANDING_PATH = "/todo";
export const TASKS_CLEARED_PATH = "/home";

/**
 * @param {object} board - the task board as currently known to the app
 * @param {string|null} completedTaskId - questionnaire just submitted; its task
 *   id is its slug
 * @returns {string} the path to navigate to
 */
export function resolvePostQuestionnaireDestination(board, completedTaskId) {
  if (!Array.isArray(board?.tasks)) {
    // No board to reason about — the To do list would have nothing to show.
    return TASKS_CLEARED_PATH;
  }

  // The board still lists this questionnaire as outstanding: it is refetched
  // after the submission, not before. Counting the submission in first is what
  // stops the questionnaire that cleared the list from being the reason the
  // user is sent back to it.
  const settled = completedTaskId
    ? applyOptimisticTaskCompletions(
        board,
        recordOptimisticTaskCompletion({}, completedTaskId, board, {
          sourceId: completedTaskId,
        }),
      )
    : board;

  const hasOutstandingTask = (settled.tasks || []).some(
    (task) =>
      task?.state === PROTOCOL_TASK_STATES.DUE ||
      task?.state === PROTOCOL_TASK_STATES.OVERDUE,
  );

  return hasOutstandingTask ? TASKS_OUTSTANDING_PATH : TASKS_CLEARED_PATH;
}
